/**
 * Phase 3 of the connect flow: which of the discovered accounts to keep.
 *
 * This dialog is the reason the flow has three phases rather than two. One Meta
 * login routinely returns several Pages, and connecting all of them is not what
 * anyone means by "connect Facebook" — so the callback stops at discovery and
 * the choice happens here, against a token the backend is holding.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import type { ConnectTarget, OAuthPendingConnection } from '@app/common';
import { Button } from '../../core/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../core/Dialog';
import { cn } from '../../../lib/utils';
import { useTranslation } from '../../../context/LanguageContext';

export function ConnectTargetDialog({
  connection,
  onConfirm,
  onDismiss,
}: Readonly<{
  connection: OAuthPendingConnection;
  onConfirm: (targetIds: string[]) => Promise<void>;
  onDismiss: () => void;
}>) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  // Pre-selecting what is already connected makes the default action "keep
  // things as they are, plus whatever else you tick" — the alternative silently
  // disconnects a working Page whenever someone reconnects to add a second one.
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(connection.targets.filter((target) => target.alreadyConnected).map((t) => t.id))
  );

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const confirm = async () => {
    setSaving(true);
    try {
      await onConfirm([...selected]);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onDismiss()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('accounts.picker.title')}</DialogTitle>
        </DialogHeader>

        <p className="mb-3 text-sm text-slate-500">{t('accounts.picker.subtitle')}</p>

        <div className="max-h-72 space-y-1.5 overflow-y-auto">
          {connection.targets.map((target) => (
            <TargetRow
              key={target.id}
              target={target}
              checked={selected.has(target.id)}
              onToggle={() => toggle(target.id)}
            />
          ))}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onDismiss} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void confirm()} disabled={saving || selected.size === 0}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('accounts.picker.confirm', { count: selected.size })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TargetRow({
  target,
  checked,
  onToggle,
}: Readonly<{ target: ConnectTarget; checked: boolean; onToggle: () => void }>) {
  const { t } = useTranslation();

  return (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 transition-colors',
        checked ? 'border-brand-300 bg-brand-50' : 'border-slate-200 hover:bg-slate-50'
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="h-4 w-4 shrink-0 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
      />

      {target.avatarUrl ? (
        <img
          src={target.avatarUrl}
          alt=""
          className="h-8 w-8 shrink-0 rounded-full object-cover"
        />
      ) : (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">
          {target.name.slice(0, 2).toUpperCase()}
        </span>
      )}

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-slate-800">{target.name}</span>
        {target.subtitle && (
          <span className="block truncate text-xs text-slate-400">{target.subtitle}</span>
        )}
      </span>

      {target.alreadyConnected && (
        <span className="shrink-0 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
          {t('accounts.picker.alreadyConnected')}
        </span>
      )}
    </label>
  );
}
